import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimate } from "framer-motion";

type CardSpec = {
  id: 2 | 3 | 4;
  img: string;
  x: number;
  y: number;
  w: number;
  z: number;
};

export default function ServiceIntroCard2Sequence() {
  const SHADOW = "drop-shadow(0 18px 38px rgba(0,0,0,0.12))";
  const START_FILTER = `blur(12px) saturate(0.95) brightness(1.02) ${SHADOW}`;
  const END_FILTER = `blur(0px) saturate(1) brightness(1) ${SHADOW}`;
  const START_OPACITY = 0.28; // 0.2~0.35 사이 취향

  const BASE_W = 623;
  const BASE_H = 604;
  const outerRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [outerWidth, setOuterWidth] = useState(BASE_W);

  const [scope, animate] = useAnimate();
  const ranRef = useRef(false);

  const scale = Math.min(outerWidth / BASE_W, 1);
  const scaledH = BASE_H * scale;

  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? BASE_W;
      setOuterWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  //  배치(필요하면 여기만 미세 조정)
  const center = { x: 130, y: 300 };
  const circleSize = 96;

  const cards: CardSpec[] = [
    {
      id: 2,
      img: "/assets/images/serviceIntro/card2_2.png",
      x: 350,
      y: 90,
      w: 221,
      z: 30,
    }, // 위
    {
      id: 3,
      img: "/assets/images/serviceIntro/card2_3.png",
      x: 380,
      y: 240,
      w: 166,
      z: 20,
    }, // 중
    {
      id: 4,
      img: "/assets/images/serviceIntro/card2_4.png",
      x: 285,
      y: 385,
      w: 310,
      z: 10,
    }, // 아래
  ];

  //  트렁크 끝(분기점)
  const branchX = 270;
  const branchY = center.y;

  const strokeBase = "#C1C5CD";
  const strokeWidth = 2;

  const lineDefs = useMemo(() => {
    // 출발점: 원 오른쪽 가장자리
    const sx = center.x + circleSize / 2 - 2;
    const sy = center.y;

    // 트렁크: 완전 직선
    const dTrunk = `M ${sx} ${sy} L ${branchX} ${branchY}`;

    // 각 카드의 “도착점”(카드 왼쪽 근처)
    const endTop = { x: cards[0].x + 8, y: cards[0].y + 26 };
    const endMid = { x: cards[1].x + 8, y: cards[1].y + 28 };
    const endBot = { x: cards[2].x + 10, y: cards[2].y + 28 };

    const bend = 50;
    const topY = branchY - bend - 100;
    const r = 16;

    const yMid = branchY + bend + 15;
    const xMid = branchX + 100;
    const yEnd = branchY + bend + 50;

    // -------------------------
    // TOP: base + grad(마지막 수평선만)
    // -------------------------
    const dTopBase = `
      M ${branchX} ${branchY}
      L ${branchX} ${topY + r}
      Q ${branchX} ${topY} ${branchX + r} ${topY}
    `;

    // 마지막 수평선만 그라데이션
    const dTopGrad = `
      M ${branchX + r} ${topY}
      L ${endTop.x} ${topY}
    `;

    // -------------------------
    // MID: 전체 그라데이션

    // -------------------------
    const midX = branchX + bend + 100;
    const dMidGrad = `
      M ${branchX} ${branchY}
      L ${midX} ${branchY}
    `;

    // -------------------------
    // BOT: base(세로 내려오기까지) + grad(yMid 이후 전체)
    // -------------------------
    const dBotBase = `
      M ${branchX} ${branchY}
      L ${branchX} ${yMid - r}
    `;

    const dBotGrad = `
      M ${branchX} ${yMid - r}
      Q ${branchX} ${yMid} ${branchX + r} ${yMid}
      L ${xMid - r} ${yMid}
      Q ${xMid} ${yMid} ${xMid} ${yMid + r}
      L ${xMid} ${yEnd}
    `;

    return {
      trunk: { d: dTrunk },
      top: {
        base: { d: dTopBase },
        grad: { d: dTopGrad, x2: endTop.x, y2: topY },
      },
      mid: {
        grad: { d: dMidGrad, x2: midX, y2: branchY },
      },
      bot: {
        base: { d: dBotBase },
        grad: { d: dBotGrad, x2: xMid, y2: yEnd },
      },
      // 그라데이션의 시작점(분기점 기준)
      gradStart: { x: branchX, y: branchY },
      endMid, // 혹시 추후 사용
      endBot, // 혹시 추후 사용
    };
  }, [cards, center.x, center.y, circleSize, branchX, branchY]);

  useEffect(() => {
    let alive = true;
    const el = hostRef.current;
    if (!el) return;

    const run = async () => {
      if (ranRef.current) return;
      ranRef.current = true;

      // 초기화: trunk
      await animate(".trunk", { pathLength: 0, opacity: 0 }, { duration: 0 });

      // 초기화: top/base+grad
      await animate(".top-base", { pathLength: 0, opacity: 0 }, { duration: 0 });
      await animate(".top-grad", { pathLength: 0, opacity: 0 }, { duration: 0 });

      // 초기화: mid/grad
      await animate(".mid-grad", { pathLength: 0, opacity: 0 }, { duration: 0 });

      // 초기화: bot/base+grad
      await animate(".bot-base", { pathLength: 0, opacity: 0 }, { duration: 0 });
      await animate(".bot-grad", { pathLength: 0, opacity: 0 }, { duration: 0 });

      // 카드 초기화
      for (const c of cards) {
        await animate(
          `.card-${c.id}`,
          {
            opacity: START_OPACITY,
            y: 14,
            scale: 0.985,
            filter: START_FILTER,
          },
          { duration: 0 },
        );
      }

      if (!alive) return;

      // 1) 트렁크
      await animate(
        ".trunk",
        { pathLength: 1, opacity: 1 },
        { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
      );

      if (!alive) return;

      // 2) 브랜치 동시 (각 브랜치의 base/grad도 함께)
      await Promise.all([
        // TOP
        animate(
          ".top-base",
          { pathLength: 1, opacity: 1 },
          { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
        ),
        animate(
          ".top-grad",
          { pathLength: 1, opacity: 1 },
          { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
        ),

        // MID
        animate(
          ".mid-grad",
          { pathLength: 1, opacity: 1 },
          { duration: 0.58, ease: [0.22, 1, 0.36, 1] },
        ),

        // BOT
        animate(
          ".bot-base",
          { pathLength: 1, opacity: 1 },
          { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        ),
        animate(
          ".bot-grad",
          { pathLength: 1, opacity: 1 },
          { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        ),
      ]);

      if (!alive) return;

      // 3) 카드 등장: 위 → 중 → 아래
      await animate(
        ".card-2",
        { opacity: 1, y: 0, scale: 1, filter: END_FILTER },
        { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
      );

      await animate(
        ".card-3",
        { opacity: 1, y: 0, scale: 1, filter: END_FILTER },
        { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.08 },
      );

      await animate(
        ".card-4",
        { opacity: 1, y: 0, scale: 1, filter: END_FILTER },
        { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 },
      );
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) run();
      },
      { threshold: 0, rootMargin: "-50% 0px -50% 0px" },
    );

    io.observe(el);

    return () => {
      alive = false;
      io.disconnect();
    };
  }, [animate, cards]);

  return (
    <section className="w-full flex justify-center">
      <div
        ref={outerRef}
        className="w-full max-w-[623px] bg-[#F2F4F8] rounded-[24px] overflow-hidden"
        style={{
          height: scaledH,
          position: "relative",
          boxShadow: "0 1.813px 19.07px 0 rgba(48, 75, 109, 0.20)",
        }}
      >
        <img
          src="/assets/images/home/card_bg.png"
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover", // 이미지가 꽉 차게
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
        <div
          ref={(node) => {
            hostRef.current = node;
            (scope as any).current = node;
          }}
          style={{
            width: BASE_W,
            height: BASE_H,
            position: "absolute",
            left: 0,
            top: -30 / scale,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {/* 라인 */}
          <svg width={BASE_W} height={BASE_H} style={{ position: "absolute", inset: 0 }}>
            <defs>
              {/* TOP: 분기점 -> top 끝 */}
              <linearGradient
                id="gradTop"
                gradientUnits="userSpaceOnUse"
                x1={lineDefs.gradStart.x}
                y1={lineDefs.gradStart.y}
                x2={lineDefs.top.grad.x2}
                y2={lineDefs.top.grad.y2}
              >
                <stop offset="0%" stopColor="#C1C5CD" />
                <stop offset="100%" stopColor="#0068FF" />
              </linearGradient>

              {/* MID: 분기점 -> mid 끝 */}
              <linearGradient
                id="gradMid"
                gradientUnits="userSpaceOnUse"
                x1={lineDefs.gradStart.x}
                y1={lineDefs.gradStart.y}
                x2={lineDefs.mid.grad.x2}
                y2={lineDefs.mid.grad.y2}
              >
                <stop offset="0%" stopColor="#C1C5CD" />
                <stop offset="100%" stopColor="#0068FF" />
              </linearGradient>

              {/* BOT: 분기점 -> bot 끝 */}
              <linearGradient
                id="gradBot"
                gradientUnits="userSpaceOnUse"
                x1={lineDefs.gradStart.x}
                y1={lineDefs.gradStart.y}
                x2={lineDefs.bot.grad.x2}
                y2={lineDefs.bot.grad.y2}
              >
                <stop offset="0%" stopColor="#C1C5CD" />
                <stop offset="100%" stopColor="#0068FF" />
              </linearGradient>
            </defs>

            {/* 트렁크(단색 유지) */}
            <motion.path
              className="trunk"
              d={lineDefs.trunk.d}
              fill="none"
              stroke={strokeBase}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
            />

            {/* TOP: base(단색) */}
            <motion.path
              className="top-base"
              d={lineDefs.top.base.d}
              fill="none"
              stroke={strokeBase}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
            />
            {/* TOP: grad(부분 그라데이션) */}
            <motion.path
              className="top-grad"
              d={lineDefs.top.grad.d}
              fill="none"
              stroke="url(#gradTop)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
            />

            {/* MID: grad(전체 그라데이션) */}
            <motion.path
              className="mid-grad"
              d={lineDefs.mid.grad.d}
              fill="none"
              stroke="url(#gradMid)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
            />

            {/* BOT: base(단색) */}
            <motion.path
              className="bot-base"
              d={lineDefs.bot.base.d}
              fill="none"
              stroke={strokeBase}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
            />
            {/* BOT: grad(부분 그라데이션) */}
            <motion.path
              className="bot-grad"
              d={lineDefs.bot.grad.d}
              fill="none"
              stroke="url(#gradBot)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
            />
          </svg>

          {/* 원 */}
          <img
            src="/assets/images/serviceIntro/card2_1.png"
            alt="card2_1"
            draggable={false}
            style={{
              position: "absolute",
              left: center.x - circleSize / 2,
              top: center.y - circleSize / 2,
              width: circleSize,
              height: circleSize,
              userSelect: "none",
              filter: "drop-shadow(0 18px 38px rgba(0,0,0,0.12))",
            }}
          />

          {/* 카드들 */}
          {cards.map((c) => (
            <motion.img
              key={c.id}
              className={`card-${c.id}`}
              src={c.img}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: c.x,
                top: c.y,
                width: c.w,
                zIndex: c.z,
                userSelect: "none",

                //  초기부터 흐릿하게 보이기
                opacity: START_OPACITY,
                transform: "translateY(14px) scale(0.985)",
                filter: START_FILTER,

                //  깜빡임/리페인트 줄이기
                willChange: "transform, opacity, filter",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
